import { useState, useMemo, useRef } from "react";
import { useSeo } from "@/hooks/useSeo";
import { BANQUET_MENU } from "@/data/banquet-menu";
import { useCreateBanquetRequest } from "@workspace/api-client-react";

type Quantities = Record<string, number>;
type TipLevel = "ok" | "warn" | "info";

interface Tip {
  level: TipLevel;
  categoryId: string;
  emoji: string;
  title: string;
  detail: string;
  actionLabel?: string;
}

function fmt(n: number) {
  return n.toLocaleString("ru-RU");
}

function plural(n: number, one: string, few: string, many: string) {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return few;
  return many;
}

function checkBirthdayPeriod(dateStr: string): boolean {
  if (!dateStr) return false;
  const parts = dateStr.split("-").map(Number);
  if (parts.length < 3) return false;
  const [, month, day] = parts;
  if (!month || !day) return false;
  const today = new Date();
  for (const year of [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1]) {
    const bday = new Date(year, month - 1, day);
    const diffDays = (today.getTime() - bday.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays >= -7 && diffDays <= 7) return true;
  }
  return false;
}

function useAdvisor(quantities: Quantities, guests: number): Tip[] {
  return useMemo(() => {
    const catById = Object.fromEntries(BANQUET_MENU.map(c => [c.id, c]));
    const sum = (catId: string) =>
      catById[catId]?.items.reduce((s, i) => s + (quantities[i.id] ?? 0), 0) ?? 0;
    const varieties = (catId: string) =>
      catById[catId]?.items.filter(i => (quantities[i.id] ?? 0) > 0).length ?? 0;

    const coldPortions = sum("cold");
    const saladVar = varieties("salads");
    const hotAppPortions = sum("hot_apps");
    const hotMainPortions = sum("hot_main");
    const grillPortions = sum("grill");
    const grillVar = varieties("grill");
    const sidesPortions = sum("sides");
    const dessertPortions = sum("desserts");
    const drinksPortions = sum("drinks");
    const totalHot = hotMainPortions + grillPortions;
    const recCold = Math.ceil(guests / 5);
    const recHotApp = Math.ceil(guests / 5);
    const tips: Tip[] = [];

    if (coldPortions === 0) {
      tips.push({ level: "warn", categoryId: "cold", emoji: "🥗", title: "Холодные закуски не выбраны",
        detail: `На ${guests} гостей рекомендуем ${recCold} порций — 1 на каждые 5 человек.`, actionLabel: "Выбрать" });
    } else if (coldPortions < recCold) {
      tips.push({ level: "warn", categoryId: "cold", emoji: "🥗", title: `Холодных закусок мало: ${coldPortions} из ${recCold}`,
        detail: `Добавьте ещё ${recCold - coldPortions} — рекомендуем 1 на каждые 5 гостей.`, actionLabel: "Добавить" });
    } else {
      tips.push({ level: "ok", categoryId: "cold", emoji: "🥗", title: "Холодные закуски — хорошо",
        detail: `${coldPortions} ${plural(coldPortions,"порция","порции","порций")} на ${guests} гостей.` });
    }

    if (saladVar === 0) {
      tips.push({ level: guests > 15 ? "warn" : "info", categoryId: "salads", emoji: "🥬", title: "Салаты не выбраны",
        detail: guests > 15 ? `При ${guests} гостях рекомендуем 2–3 разных вида салата.` : `Добавьте 1–2 вида.`, actionLabel: "Выбрать" });
    } else if (guests > 15 && saladVar < 2) {
      tips.push({ level: "warn", categoryId: "salads", emoji: "🥬", title: `Только ${saladVar} вид салата`,
        detail: `При ${guests} гостях рекомендуем 2–3 разных вида.`, actionLabel: "Добавить" });
    } else {
      tips.push({ level: "ok", categoryId: "salads", emoji: "🥬", title: `Салаты — ${saladVar} ${plural(saladVar,"вид","вида","видов")}`,
        detail: "Хорошее разнообразие." });
    }

    if (hotAppPortions === 0) {
      tips.push({ level: "warn", categoryId: "hot_apps", emoji: "🦐", title: "Горячие закуски не выбраны",
        detail: `Рекомендуем ${recHotApp} порций — 1 на каждые 5 гостей.`, actionLabel: "Выбрать" });
    } else if (hotAppPortions < recHotApp) {
      tips.push({ level: "warn", categoryId: "hot_apps", emoji: "🦐", title: `Горячих закусок: ${hotAppPortions} из ${recHotApp}`,
        detail: `Добавьте ещё ${recHotApp - hotAppPortions} — 1 на каждые 5 гостей.`, actionLabel: "Добавить" });
    } else {
      tips.push({ level: "ok", categoryId: "hot_apps", emoji: "🦐", title: "Горячие закуски — хорошо",
        detail: `${hotAppPortions} ${plural(hotAppPortions,"порция","порции","порций")} на ${guests} гостей.` });
    }

    if (totalHot === 0) {
      tips.push({ level: "info", categoryId: "hot_main", emoji: "🍽️", title: "Горячее главное не выбрано",
        detail: `Добавьте горячее — порционно (1 на гостя) или несколько видов в стол.`, actionLabel: "Выбрать" });
    } else if (hotMainPortions > 0 && hotMainPortions < guests) {
      tips.push({ level: "info", categoryId: "hot_main", emoji: "🍽️", title: `Горячих: ${hotMainPortions} из ${guests}`,
        detail: `При порционной подаче нужно 1 на гостя. Не хватает ${guests - hotMainPortions}.`, actionLabel: "Добавить" });
    } else if (hotMainPortions >= guests) {
      tips.push({ level: "ok", categoryId: "hot_main", emoji: "🍽️", title: "Горячие блюда — хорошо",
        detail: `${hotMainPortions} ${plural(hotMainPortions,"порция","порции","порций")} — каждому гостю хватит.` });
    }

    if (grillVar >= 2) {
      const perType = Math.ceil(guests / grillVar);
      const grillItems = catById["grill"].items.filter(i => (quantities[i.id] ?? 0) > 0);
      const unbalanced = grillItems.filter(i => (quantities[i.id] ?? 0) < Math.floor(perType * 0.7));
      if (unbalanced.length > 0) {
        tips.push({ level: "info", categoryId: "grill", emoji: "🥩", title: `Ассорти гриля — проверьте баланс`,
          detail: `При ${grillVar} видах на ${guests} гостей → ~${perType} порций каждого вида. Некоторых позиций меньше нормы.`, actionLabel: "Проверить" });
      } else {
        tips.push({ level: "ok", categoryId: "grill", emoji: "🥩", title: "Ассорти гриля — баланс отличный",
          detail: `${grillVar} вида × ~${perType} порций — хватит на всех.` });
      }
    } else if (grillVar === 1) {
      const grillItem = catById["grill"].items.find(i => (quantities[i.id] ?? 0) > 0);
      if (grillItem && (quantities[grillItem.id] ?? 0) < guests) {
        tips.push({ level: "info", categoryId: "grill", emoji: "🥩", title: `Порций гриля: ${quantities[grillItem.id]} из ${guests}`,
          detail: `При одном виде рекомендуем 1 порцию на гостя.`, actionLabel: "Добавить" });
      }
    }

    if (totalHot > 0 && sidesPortions === 0) {
      tips.push({ level: "info", categoryId: "sides", emoji: "🥔", title: "Гарниры не выбраны",
        detail: `К горячим блюдам рекомендуем 1–2 вида гарниров.`, actionLabel: "Выбрать" });
    } else if (sidesPortions > 0) {
      tips.push({ level: "ok", categoryId: "sides", emoji: "🥔", title: "Гарниры — выбраны", detail: "" });
    }

    if (dessertPortions === 0) {
      tips.push({ level: "info", categoryId: "desserts", emoji: "🍰", title: "Десерты не выбраны",
        detail: `Рекомендуем ~1 порцию на гостя (${guests} шт).`, actionLabel: "Выбрать" });
    } else if (dessertPortions < guests) {
      tips.push({ level: "info", categoryId: "desserts", emoji: "🍰", title: `Десертов: ${dessertPortions} из ${guests}`,
        detail: `Рекомендуем по 1 на гостя — добавьте ещё ${guests - dessertPortions}.`, actionLabel: "Добавить" });
    } else {
      tips.push({ level: "ok", categoryId: "desserts", emoji: "🍰", title: "Десерты — хорошо",
        detail: `${dessertPortions} ${plural(dessertPortions,"порция","порции","порций")} — всем достанется.` });
    }

    if (drinksPortions === 0) {
      tips.push({ level: "warn", categoryId: "drinks", emoji: "🥂", title: "Напитки не выбраны",
        detail: `Не забудьте воду и напитки.`, actionLabel: "Выбрать" });
    } else {
      tips.push({ level: "ok", categoryId: "drinks", emoji: "🥂", title: "Напитки — выбраны", detail: "" });
    }

    return tips;
  }, [quantities, guests]);
}

const KITCHEN_CATS = new Set(["cold","salads","hot_apps","hot_main","company","grill","sides","pizza","extras","desserts","kids"]);
const BAR_CATS     = new Set(["drinks"]);

function printOrderForm(params: {
  clientName: string;
  clientPhone: string;
  eventDate: string;
  guests: number;
  note: string;
  selectedItems: { categoryTitle: string; name: string; qty: number; price: number; total: number }[];
  grandTotal: number;
  discountAmount: number;
  serviceChargeAmount: number;
  serviceChargePct: number;
  finalTotal: number;
  finalPerPerson: number;
  paymentMethod: "card" | "bank";
  birthdayValid: boolean;
}) {
  const {
    clientName, clientPhone, eventDate, guests, note,
    selectedItems, grandTotal, discountAmount, serviceChargeAmount,
    serviceChargePct, finalTotal, finalPerPerson, paymentMethod, birthdayValid,
  } = params;

  const now = new Date();
  const orderNum = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,"0")}${String(now.getDate()).padStart(2,"0")}-${String(now.getHours()).padStart(2,"0")}${String(now.getMinutes()).padStart(2,"0")}`;
  const fmtNum = (n: number) => n.toLocaleString("ru-RU");
  const nowStr = now.toLocaleString("ru-RU", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" });

  function renderBlock(catIds: Set<string>, title: string) {
    const items = selectedItems.filter(it => {
      const cat = BANQUET_MENU.find(c => c.title === it.categoryTitle);
      return cat && catIds.has(cat.id);
    });
    if (items.length === 0) return "";
    let html = `<div class="block-header">${title}</div>`;
    let lastCat = "";
    for (const it of items) {
      if (it.categoryTitle !== lastCat) {
        html += `<div class="cat-title">${it.categoryTitle}</div>`;
        lastCat = it.categoryTitle;
      }
      html += `<div class="row">
        <span class="row-name">${it.name}</span>
        <span class="row-qty">× ${it.qty}</span>
        <span class="row-total">${fmtNum(it.total)} ₽</span>
      </div>`;
    }
    return html;
  }

  const kitchenHtml = renderBlock(KITCHEN_CATS, "🍽 КУХНЯ");
  const barHtml     = renderBlock(BAR_CATS,     "🍸 БАР");

  const html = `<!DOCTYPE html>
<html lang="ru">
<head>
  <meta charset="UTF-8"/>
  <title>Заказ-наряд ${orderNum}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Arial', sans-serif; font-size: 12px; color: #111; background: #fff; padding: 20px; max-width: 800px; margin: 0 auto; }
    @media print {
      body { padding: 10px; }
      .no-print { display: none !important; }
    }

    .header { text-align: center; border-bottom: 2px solid #111; padding-bottom: 10px; margin-bottom: 14px; }
    .header h1 { font-size: 20px; font-weight: bold; letter-spacing: 2px; text-transform: uppercase; }
    .header p { font-size: 11px; color: #555; margin-top: 3px; }
    .order-meta { font-size: 13px; font-weight: bold; margin-top: 6px; }

    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 20px; border: 1px solid #ccc; padding: 10px 14px; margin-bottom: 14px; }
    .info-row { display: flex; gap: 6px; align-items: baseline; }
    .info-label { font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; white-space: nowrap; }
    .info-value { font-size: 13px; font-weight: bold; border-bottom: 1px solid #ccc; flex: 1; min-width: 80px; padding-bottom: 1px; }

    .block-header { background: #111; color: #fff; font-size: 11px; font-weight: bold; letter-spacing: 1px; text-transform: uppercase; padding: 5px 10px; margin-top: 14px; margin-bottom: 4px; }
    .cat-title { font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; padding: 6px 10px 2px; border-top: 1px solid #e0e0e0; margin-top: 4px; }
    .row { display: flex; align-items: baseline; gap: 4px; padding: 4px 10px; border-bottom: 1px dotted #e0e0e0; }
    .row:last-child { border-bottom: none; }
    .row-name { flex: 1; font-size: 12px; }
    .row-qty { font-size: 12px; font-weight: bold; white-space: nowrap; width: 40px; text-align: center; color: #333; }
    .row-total { font-size: 12px; white-space: nowrap; width: 90px; text-align: right; font-weight: bold; }

    .pricing { border: 1px solid #111; margin-top: 16px; }
    .pricing-header { background: #111; color: #fff; font-size: 11px; font-weight: bold; letter-spacing: 1px; text-transform: uppercase; padding: 5px 10px; }
    .pricing-row { display: flex; justify-content: space-between; padding: 5px 14px; border-bottom: 1px dotted #e0e0e0; font-size: 12px; }
    .pricing-row:last-child { border-bottom: none; }
    .pricing-row.discount { color: #1a7a4a; font-weight: bold; }
    .pricing-row.surcharge { color: #b45309; }
    .pricing-row.total { background: #f5f5f5; font-size: 15px; font-weight: bold; border-top: 2px solid #111; }
    .pricing-row.per-person { font-size: 11px; color: #555; }

    .note-box { border: 1px solid #ccc; padding: 8px 12px; margin-top: 14px; min-height: 48px; }
    .note-label { font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }

    .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 30px; margin-top: 20px; }
    .sig-block { border-top: 1px solid #111; padding-top: 4px; }
    .sig-label { font-size: 10px; color: #555; text-transform: uppercase; letter-spacing: 0.5px; }
    .sig-line { margin-top: 18px; border-top: 1px solid #ccc; font-size: 9px; color: #999; text-align: center; padding-top: 2px; }

    .print-btn { display: inline-block; margin: 0 0 18px; padding: 8px 24px; background: #111; color: #fff; border: none; cursor: pointer; font-size: 13px; font-weight: bold; letter-spacing: 1px; text-transform: uppercase; }
    .footer-note { font-size: 10px; color: #999; text-align: center; margin-top: 20px; border-top: 1px solid #e0e0e0; padding-top: 8px; }
  </style>
</head>
<body>
  <div class="no-print" style="margin-bottom:16px">
    <button class="print-btn" onclick="window.print()">🖨 Распечатать бланк</button>
    <span style="font-size:11px;color:#888;margin-left:12px">Ctrl+P или кнопка выше</span>
  </div>

  <div class="header">
    <h1>Паруса на крыше</h1>
    <p>ул. Марата 5, 6 этаж, Olympic Plaza, Санкт-Петербург</p>
    <p class="order-meta">ЗАКАЗ-НАРЯД № ${orderNum} &nbsp;|&nbsp; ${nowStr}</p>
  </div>

  <div class="info-grid">
    <div class="info-row"><span class="info-label">Гость:</span><span class="info-value">${clientName || "—"}</span></div>
    <div class="info-row"><span class="info-label">Телефон:</span><span class="info-value">${clientPhone || "—"}</span></div>
    <div class="info-row"><span class="info-label">Дата мероприятия:</span><span class="info-value">${eventDate || "—"}</span></div>
    <div class="info-row"><span class="info-label">Гостей:</span><span class="info-value">${guests} чел.</span></div>
    <div class="info-row"><span class="info-label">Оплата:</span><span class="info-value">${paymentMethod === "bank" ? "Расчётный счёт (р/с)" : "Наличные / Банковская карта"}</span></div>
    <div class="info-row"><span class="info-label">Скидка именинника:</span><span class="info-value">${birthdayValid ? "Да −20% (паспорт предъявлен)" : "Нет"}</span></div>
  </div>

  ${kitchenHtml}
  ${barHtml}

  <div class="pricing">
    <div class="pricing-header">💰 Расчёт</div>
    <div class="pricing-row"><span>Сумма блюд</span><span>${fmtNum(grandTotal)} ₽</span></div>
    ${discountAmount > 0 ? `<div class="pricing-row discount"><span>Скидка именинника −20%</span><span>−${fmtNum(discountAmount)} ₽</span></div>` : ""}
    ${serviceChargeAmount > 0 ? `<div class="pricing-row surcharge"><span>Сервисный сбор ${serviceChargePct * 100}%${paymentMethod === "bank" ? " (р/с)" : " (группа)"}</span><span>+${fmtNum(serviceChargeAmount)} ₽</span></div>` : ""}
    <div class="pricing-row total"><span>К ОПЛАТЕ</span><span>${fmtNum(finalTotal)} ₽</span></div>
    ${guests > 0 ? `<div class="pricing-row per-person"><span>На человека (~${guests} гостей)</span><span>~${fmtNum(finalPerPerson)} ₽</span></div>` : ""}
  </div>

  ${note ? `<div class="note-box"><div class="note-label">Примечания</div><div>${note}</div></div>` : `<div class="note-box"><div class="note-label">Примечания</div></div>`}

  <div class="signatures">
    <div class="sig-block">
      <div class="sig-label">Гость</div>
      <div class="sig-line">подпись / дата</div>
    </div>
    <div class="sig-block">
      <div class="sig-label">Менеджер ресторана</div>
      <div class="sig-line">подпись / дата</div>
    </div>
    <div class="sig-block">
      <div class="sig-label">Шеф-повар / Кухня</div>
      <div class="sig-line">подпись / дата</div>
    </div>
    <div class="sig-block">
      <div class="sig-label">Бармен / Бар</div>
      <div class="sig-line">подпись / дата</div>
    </div>
  </div>

  <div class="footer-note">Паруса на крыше · parusanakryshe.ru · Документ сформирован ${nowStr}</div>
</body>
</html>`;

  const win = window.open("", "_blank", "width=900,height=800");
  if (!win) return;
  win.document.write(html);
  win.document.close();
}

const TIP_STYLE: Record<TipLevel, { bg: string; border: string; iconBg: string; icon: string; titleColor: string }> = {
  ok:   { bg: "bg-emerald-950/40", border: "border-emerald-800/30", iconBg: "bg-emerald-600", icon: "✓", titleColor: "text-emerald-400" },
  warn: { bg: "bg-amber-950/40",   border: "border-amber-700/40",   iconBg: "bg-amber-500",   icon: "!", titleColor: "text-amber-300"  },
  info: { bg: "bg-sky-950/40",     border: "border-sky-800/30",     iconBg: "bg-sky-600",     icon: "→", titleColor: "text-sky-400"    },
};

export default function MenuConstructor() {
  useSeo({
    title: "Конструктор банкетного меню — Паруса на крыше",
    description: "Составьте меню вашего мероприятия — выберите блюда и получите точную стоимость.",
    canonical: "/menu-constructor",
  });

  const [guests, setGuests] = useState(20);
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [note, setNote] = useState("");
  const [quantities, setQuantities] = useState<Quantities>({});
  const [activeCategory, setActiveCategory] = useState(BANQUET_MENU[0].id);
  const [submitted, setSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [advisorOpen, setAdvisorOpen] = useState(false);
  const [birthdayDiscount, setBirthdayDiscount] = useState(false);
  const [birthdayDate, setBirthdayDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"card" | "bank">("card");
  const summaryRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const createBanquet = useCreateBanquetRequest();
  const tips = useAdvisor(quantities, guests);
  const warnCount = tips.filter(t => t.level === "warn").length;
  const allOk = warnCount === 0;

  const change = (id: string, delta: number) => {
    setQuantities(prev => {
      const next = (prev[id] ?? 0) + delta;
      if (next <= 0) { const { [id]: _, ...rest } = prev; return rest; }
      return { ...prev, [id]: next };
    });
  };

  const selectedItems = useMemo(() => {
    const result: { categoryTitle: string; name: string; qty: number; price: number; total: number }[] = [];
    for (const cat of BANQUET_MENU) {
      for (const item of cat.items) {
        const qty = quantities[item.id] ?? 0;
        if (qty > 0) result.push({ categoryTitle: cat.title, name: item.name, qty, price: item.price, total: qty * item.price });
      }
    }
    return result;
  }, [quantities]);

  const grandTotal = useMemo(() => selectedItems.reduce((s, i) => s + i.total, 0), [selectedItems]);
  const totalPortions = Object.values(quantities).reduce((s, v) => s + v, 0);

  const birthdayValid = birthdayDiscount && checkBirthdayPeriod(birthdayDate);
  const discountPct = birthdayValid ? 0.20 : 0;
  const serviceChargePct = paymentMethod === "bank" ? 0.15 : guests > 5 ? 0.10 : 0;
  const discountAmount = Math.round(grandTotal * discountPct);
  const afterDiscount = grandTotal - discountAmount;
  const serviceChargeAmount = Math.round(afterDiscount * serviceChargePct);
  const finalTotal = afterDiscount + serviceChargeAmount;
  const finalPerPerson = guests > 0 ? Math.round(finalTotal / guests) : 0;

  function buildComment() {
    const lines = [`[ПРЕДЗАКАЗ МЕНЮ A-LA CARTE]`, `Гостей: ${guests}`, ``, `── ВЫБРАННЫЕ ПОЗИЦИИ ──`];
    let lastCat = "";
    for (const it of selectedItems) {
      if (it.categoryTitle !== lastCat) { lines.push(`\n${it.categoryTitle}:`); lastCat = it.categoryTitle; }
      lines.push(`  ${it.name} × ${it.qty} = ${fmt(it.total)} ₽`);
    }
    lines.push(``, `── РАСЧЁТ ──`);
    lines.push(`Сумма блюд: ${fmt(grandTotal)} ₽`);
    if (discountAmount > 0) lines.push(`Скидка именинника −20%: −${fmt(discountAmount)} ₽ (требуется паспорт)`);
    if (serviceChargeAmount > 0) lines.push(`Сервисный сбор ${serviceChargePct * 100}% (${paymentMethod === "bank" ? "оплата по р/с" : "компания >5 человек"}): +${fmt(serviceChargeAmount)} ₽`);
    lines.push(`К оплате: ${fmt(finalTotal)} ₽`);
    lines.push(`На человека: ~${fmt(finalPerPerson)} ₽`);
    lines.push(`Способ оплаты: ${paymentMethod === "bank" ? "расчётный счёт (р/с)" : "наличные / карта"}`);
    if (note) lines.push(``, `Примечание: ${note}`);
    return lines.join("\n");
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!clientName || !clientPhone || !consent || selectedItems.length === 0) return;
    createBanquet.mutate({
      data: { name: clientName, phone: clientPhone, eventType: "Предзаказ меню",
        date: eventDate || "не указана", guests: String(guests), comment: buildComment(), consent: true },
    }, { onSuccess: () => setSubmitted(true) });
  }

  const scrollToSummary = () => summaryRef.current?.scrollIntoView({ behavior: "smooth" });
  const goToCategory = (catId: string) => {
    setActiveCategory(catId);
    setAdvisorOpen(false);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="w-full bg-background min-h-screen">
      {/* Offset for fixed Layout navbar (h-20 logo + py-8 padding ≈ 144px, py-4 scrolled ≈ 112px) */}
      <div className="pt-36 md:pt-40" ref={topRef} />

      <div className="container mx-auto px-4 md:px-10 max-w-7xl pb-40">

        {/* ── Page title + guest counter ── */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 border-b border-primary/15 pb-6">
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase text-primary mb-1">Паруса на крыше</p>
            <h1 className="text-2xl md:text-3xl font-serif font-light text-foreground">Конструктор банкетного меню</h1>
            <p className="text-xs text-muted-foreground/50 mt-1 font-light">Выберите блюда — стоимость считается автоматически</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground/60 uppercase tracking-wide">Гостей:</span>
            <div className="flex items-center border border-primary/30">
              <button onClick={() => setGuests(g => Math.max(1, g - 5))}
                className="w-9 h-9 text-lg text-muted-foreground hover:text-accent transition-colors">−</button>
              <input type="number" value={guests} onChange={e => setGuests(Math.max(1, Number(e.target.value)))}
                className="w-14 text-center bg-transparent text-foreground font-medium text-sm focus:outline-none py-2" />
              <button onClick={() => setGuests(g => g + 5)}
                className="w-9 h-9 text-lg text-muted-foreground hover:text-accent transition-colors">+</button>
            </div>
            <button onClick={scrollToSummary}
              className="relative bg-primary/20 border border-primary text-accent px-4 py-2 text-[9px] tracking-[0.2em] uppercase hover:bg-primary hover:text-primary-foreground transition-colors">
              Итого
              {totalPortions > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-accent text-background text-[8px] rounded-full flex items-center justify-center font-bold">
                  {totalPortions > 99 ? "99+" : totalPortions}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* ── Category tabs ── */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide">
          {BANQUET_MENU.map(cat => {
            const catTotal = cat.items.reduce((s, item) => s + (quantities[item.id] ?? 0), 0);
            const catTip = tips.find(t => t.categoryId === cat.id);
            const hasWarn = catTip?.level === "warn" && catTotal === 0;
            const isActive = activeCategory === cat.id;
            return (
              <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs border whitespace-nowrap transition-all shrink-0 font-medium
                  ${isActive
                    ? "border-accent bg-accent text-background shadow-[0_0_12px_rgba(0,200,150,0.25)]"
                    : catTotal > 0
                      ? "border-primary/60 bg-primary/15 text-foreground hover:border-primary hover:bg-primary/25"
                      : "border-white/20 bg-white/5 text-foreground/75 hover:border-white/40 hover:text-foreground hover:bg-white/10"}`}>
                <span>{cat.emoji}</span>
                <span>{cat.title}</span>
                {catTotal > 0 && (
                  <span className={`ml-1 w-5 h-5 text-[9px] rounded-full flex items-center justify-center font-bold ${isActive ? "bg-background/30 text-background" : "bg-accent/30 text-accent"}`}>{catTotal}</span>
                )}
                {hasWarn && !catTotal && (
                  <span className="ml-1 w-2 h-2 bg-amber-400 rounded-full shrink-0" />
                )}
              </button>
            );
          })}
        </div>

        {/* ── Active category ── */}
        {BANQUET_MENU.filter(cat => cat.id === activeCategory).map(cat => {
          const catTip = tips.find(t => t.categoryId === cat.id);
          return (
            <div key={cat.id}>
              <div className="mb-5 flex items-start gap-3">
                <span className="text-2xl mt-0.5">{cat.emoji}</span>
                <div className="flex-1">
                  <h2 className="text-2xl font-serif font-light text-foreground">{cat.title}</h2>
                  {cat.note && <p className="text-xs text-muted-foreground/60 mt-0.5">{cat.note}</p>}
                </div>
              </div>

              {catTip && catTip.level !== "ok" && (
                <div className={`mb-5 flex items-start gap-3 border px-4 py-3 rounded-sm ${TIP_STYLE[catTip.level].bg} ${TIP_STYLE[catTip.level].border}`}>
                  <span className="text-lg mt-0.5">{catTip.level === "warn" ? "⚠️" : "💡"}</span>
                  <div>
                    <p className={`text-xs font-medium ${TIP_STYLE[catTip.level].titleColor}`}>{catTip.title}</p>
                    {catTip.detail && <p className="text-[11px] text-muted-foreground/60 mt-0.5">{catTip.detail}</p>}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {cat.items.map(item => {
                  const qty = quantities[item.id] ?? 0;
                  return (
                    <div key={item.id}
                      className={`border p-4 transition-all ${qty > 0 ? "border-accent/40 bg-primary/10" : "border-primary/15 hover:border-primary/30"}`}>
                      <div className="flex justify-between items-start gap-3 mb-3">
                        <div className="flex-1">
                          <p className={`text-sm font-light leading-snug ${qty > 0 ? "text-foreground" : "text-foreground/80"}`}>{item.name}</p>
                          {item.description && (
                            <p className="text-[11px] text-muted-foreground/55 mt-1.5 leading-relaxed font-light">{item.description}</p>
                          )}
                          {item.weight && <p className="text-[10px] text-primary/40 mt-1">{item.weight}</p>}
                        </div>
                        <span className={`text-sm font-medium whitespace-nowrap shrink-0 ${qty > 0 ? "text-accent" : "text-muted-foreground/70"}`}>
                          {fmt(item.price)} ₽
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        {qty > 0 && <span className="text-[10px] text-accent/70">{fmt(qty * item.price)} ₽</span>}
                        <div className={`flex items-center ml-auto ${qty > 0 ? "border border-accent/40" : "border border-primary/20"}`}>
                          {qty > 0 && (
                            <button onClick={() => change(item.id, -1)}
                              className="w-9 h-9 text-muted-foreground hover:text-accent transition-colors text-base">−</button>
                          )}
                          {qty > 0 && (
                            <span className="w-9 h-9 flex items-center justify-center text-accent font-medium text-sm">{qty}</span>
                          )}
                          <button onClick={() => change(item.id, 1)}
                            className={`h-9 transition-colors ${qty > 0 ? "w-9 text-base text-muted-foreground hover:text-accent" : "px-4 text-[10px] uppercase tracking-wide text-muted-foreground hover:text-accent"}`}>
                            {qty === 0 ? "Добавить" : "+"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* ── Summary + form ── */}
        <div ref={summaryRef} className="mt-16 border-t border-primary/20 pt-12">
          {selectedItems.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground/40 text-sm">Вы ещё не выбрали ни одного блюда</p>
              <p className="text-muted-foreground/30 text-xs mt-2">Выберите позиции из меню выше</p>
            </div>
          ) : (
            <>
              <div className="mb-10">
                <h2 className="text-3xl font-serif font-light text-foreground mb-8">Ваш заказ</h2>
                <div className="border border-primary/20">
                  {(() => {
                    let lastCat = "";
                    return selectedItems.map((it, idx) => {
                      const showCatHeader = it.categoryTitle !== lastCat;
                      lastCat = it.categoryTitle;
                      return (
                        <div key={idx}>
                          {showCatHeader && (
                            <div className="px-5 py-2 bg-primary/5 border-b border-primary/10">
                              <p className="text-[9px] tracking-[0.2em] uppercase text-primary/60">{it.categoryTitle}</p>
                            </div>
                          )}
                          <div className="flex items-center justify-between px-5 py-3 border-b border-primary/10 last:border-0 gap-4">
                            <span className="text-sm text-foreground/80 font-light flex-1">{it.name}</span>
                            <span className="text-xs text-muted-foreground/50 whitespace-nowrap">× {it.qty}</span>
                            <span className="text-sm text-foreground/70 whitespace-nowrap font-medium w-28 text-right">{fmt(it.total)} ₽</span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* ── Pricing conditions ── */}
                <div className="mt-6 border border-primary/20 divide-y divide-primary/10">

                  {/* Birthday discount */}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-base">🎂</span>
                          <p className="text-sm text-foreground font-light">Скидка именинника −20%</p>
                          {birthdayValid && (
                            <span className="text-[9px] px-2 py-0.5 bg-emerald-950/60 border border-emerald-800/40 text-emerald-400 uppercase tracking-wide">Применена</span>
                          )}
                          {birthdayDiscount && birthdayDate && !birthdayValid && (
                            <span className="text-[9px] px-2 py-0.5 bg-amber-950/60 border border-amber-800/40 text-amber-400 uppercase tracking-wide">Дата не в периоде ±7 дней</span>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground/50 leading-relaxed">
                          Действует за 7 дней до и 7 дней после дня рождения. Необходимо предъявить паспорт администратору.
                        </p>
                      </div>
                      <button onClick={() => setBirthdayDiscount(v => !v)}
                        className={`shrink-0 w-12 h-6 rounded-full transition-colors relative ${birthdayDiscount ? "bg-primary" : "bg-primary/20"}`}>
                        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${birthdayDiscount ? "left-6" : "left-0.5"}`} />
                      </button>
                    </div>
                    {birthdayDiscount && (
                      <div className="mt-3 flex items-center gap-3">
                        <label className="text-[10px] uppercase tracking-wide text-muted-foreground/60 whitespace-nowrap">Дата рождения:</label>
                        <input type="date" value={birthdayDate} onChange={e => setBirthdayDate(e.target.value)}
                          className="bg-transparent border-b border-primary/30 text-foreground text-sm focus:outline-none focus:border-primary pb-1 transition-colors" />
                      </div>
                    )}
                  </div>

                  {/* Payment method */}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-base">💳</span>
                      <p className="text-sm text-foreground font-light">Способ оплаты</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground/50 mb-3 leading-relaxed">
                      Для групп более 5 человек — сервисный сбор 10%. При оплате по расчётному счёту — 15%.
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => setPaymentMethod("card")}
                        className={`flex-1 py-2.5 text-[10px] uppercase tracking-wide border transition-all ${paymentMethod === "card" ? "border-accent bg-primary/20 text-accent" : "border-primary/20 text-muted-foreground hover:border-primary/40"}`}>
                        💴 Наличные / Карта
                        {paymentMethod === "card" && guests > 5 && (
                          <span className="block text-[9px] text-amber-400 mt-0.5 normal-case tracking-normal">+ сервисный сбор 10%</span>
                        )}
                      </button>
                      <button onClick={() => setPaymentMethod("bank")}
                        className={`flex-1 py-2.5 text-[10px] uppercase tracking-wide border transition-all ${paymentMethod === "bank" ? "border-accent bg-primary/20 text-accent" : "border-primary/20 text-muted-foreground hover:border-primary/40"}`}>
                        🏦 Расчётный счёт (р/с)
                        {paymentMethod === "bank" && (
                          <span className="block text-[9px] text-amber-400 mt-0.5 normal-case tracking-normal">+ сервисный сбор 15%</span>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Price breakdown */}
                  <div className="p-4 bg-primary/5">
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground/70">Сумма блюд</span>
                        <span className="text-sm text-foreground/80">{fmt(grandTotal)} ₽</span>
                      </div>
                      {discountAmount > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-emerald-400">Скидка именинника −20%</span>
                          <span className="text-sm text-emerald-400 font-medium">−{fmt(discountAmount)} ₽</span>
                        </div>
                      )}
                      {serviceChargeAmount > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-amber-400/80">
                            Сервисный сбор {serviceChargePct * 100}%
                            <span className="text-muted-foreground/50 ml-1">
                              ({paymentMethod === "bank" ? "оплата по р/с" : `компания ${guests} чел.`})
                            </span>
                          </span>
                          <span className="text-sm text-amber-400/80">+{fmt(serviceChargeAmount)} ₽</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2 border-t border-primary/20">
                        <div>
                          <p className="text-[9px] tracking-[0.2em] uppercase text-primary/60 mb-0.5">К оплате</p>
                          {guests > 0 && (
                            <p className="text-[10px] text-muted-foreground/50">~{fmt(finalPerPerson)} ₽ на человека</p>
                          )}
                        </div>
                        <p className="font-serif text-3xl text-accent">{fmt(finalTotal)} ₽</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {submitted ? (
                <div className="border border-accent/30 bg-primary/10 p-8 md:p-10">
                  <div className="flex flex-col items-center text-center mb-8">
                    <div className="w-12 h-12 border border-accent rounded-full flex items-center justify-center mb-5 text-accent text-xl">✓</div>
                    <h3 className="text-2xl font-serif font-light text-primary mb-2">Заказ отправлен</h3>
                    <p className="text-sm text-muted-foreground font-light">Банкетный менеджер свяжется с вами для подтверждения.</p>
                  </div>
                  <div className="border-t border-primary/20 pt-6">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-primary/60 mb-4 text-center">Бланк для кухни, бара и менеджера</p>
                    <button
                      onClick={() => printOrderForm({
                        clientName, clientPhone, eventDate, guests, note,
                        selectedItems, grandTotal, discountAmount, serviceChargeAmount,
                        serviceChargePct, finalTotal, finalPerPerson, paymentMethod, birthdayValid,
                      })}
                      className="w-full flex items-center justify-center gap-3 border border-accent/60 bg-accent/10 text-accent hover:bg-accent hover:text-background py-4 text-[11px] uppercase tracking-[0.25em] font-medium transition-all">
                      🖨 Сформировать и распечатать бланк-наряд
                    </button>
                    <p className="text-[10px] text-muted-foreground/40 text-center mt-3">
                      Откроется окно предпросмотра — распечатайте в 3 экземплярах: кухня, бар, менеджер
                    </p>
                  </div>
                </div>
              ) : (
                <div className="border border-primary/20 bg-card p-8 md:p-10">
                  <h3 className="text-2xl font-serif font-light text-foreground mb-8">Подтвердить заказ</h3>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60 block mb-2">Ваше имя</label>
                        <input value={clientName} onChange={e => setClientName(e.target.value)} required
                          className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground font-light text-lg focus:outline-none focus:border-primary transition-colors" />
                      </div>
                      <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60 block mb-2">Телефон</label>
                        <input type="tel" value={clientPhone} onChange={e => setClientPhone(e.target.value)} required
                          className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground font-light text-lg focus:outline-none focus:border-primary transition-colors" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60 block mb-2">Дата мероприятия</label>
                        <input value={eventDate} onChange={e => setEventDate(e.target.value)} placeholder="Например: 15 августа 2025"
                          className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground font-light text-lg focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/30" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60 block mb-2">Дополнительные пожелания</label>
                      <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                        placeholder="Аллергии, особые пожелания по подаче..."
                        className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground font-light text-sm focus:outline-none focus:border-primary transition-colors resize-none placeholder:text-muted-foreground/30" />
                    </div>
                    <div className="flex items-start gap-3">
                      <input type="checkbox" id="consent-mc" checked={consent} onChange={e => setConsent(e.target.checked)}
                        className="mt-1 w-4 h-4 accent-primary shrink-0 cursor-pointer" required />
                      <label htmlFor="consent-mc" className="text-xs font-light text-muted-foreground leading-relaxed cursor-pointer">
                        Я даю согласие на обработку персональных данных
                      </label>
                    </div>
                    <button type="submit" disabled={createBanquet.isPending || !consent || selectedItems.length === 0}
                      className="w-full bg-primary/20 border border-primary text-accent hover:bg-primary hover:text-primary-foreground text-[10px] py-5 uppercase tracking-[0.3em] font-medium transition-colors disabled:opacity-40">
                      {createBanquet.isPending ? "Отправка..." : `Отправить заказ — ${fmt(grandTotal)} ₽`}
                    </button>
                  </form>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Floating advisor button (fixed, bottom-right, above bottom bar) ── */}
      <div className={`fixed right-4 z-[55] transition-all duration-200 ${grandTotal > 0 ? "bottom-24" : "bottom-8"}`}>
        <div className="relative">
          {/* Advisor panel */}
          {advisorOpen && (
            <div className="absolute bottom-14 right-0 w-80 max-h-[70vh] overflow-y-auto bg-background/98 border border-primary/30 shadow-2xl">
              <div className="p-4 border-b border-primary/20 flex items-center justify-between">
                <div>
                  <p className="text-[9px] tracking-[0.2em] uppercase text-primary/60 mb-0.5">🤖 Советник по меню</p>
                  <p className="text-xs text-foreground font-light">
                    {allOk ? "Заказ сбалансирован 👍" : `${warnCount} важных рекомендации`}
                  </p>
                </div>
                <button onClick={() => setAdvisorOpen(false)} className="text-muted-foreground/40 hover:text-muted-foreground text-lg w-6 h-6 flex items-center justify-center">×</button>
              </div>
              <div className="p-3 space-y-2">
                {tips.map((tip, i) => {
                  const c = TIP_STYLE[tip.level];
                  return (
                    <div key={i} className={`border p-3 ${c.bg} ${c.border}`}>
                      <div className="flex items-start gap-2 mb-2">
                        <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 mt-0.5 text-white ${c.iconBg}`}>
                          {c.icon}
                        </span>
                        <div>
                          <p className={`text-[11px] font-medium leading-tight ${c.titleColor}`}>{tip.emoji} {tip.title}</p>
                          {tip.detail && <p className="text-[10px] text-muted-foreground/55 mt-1 leading-relaxed">{tip.detail}</p>}
                        </div>
                      </div>
                      {tip.actionLabel && tip.level !== "ok" && (
                        <button onClick={() => goToCategory(tip.categoryId)}
                          className={`text-[9px] uppercase tracking-wide border px-2.5 py-1 transition-colors ${c.titleColor} ${c.border} hover:opacity-80`}>
                          {tip.actionLabel} →
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Advisor toggle button */}
          <button onClick={() => setAdvisorOpen(v => !v)}
            className={`flex items-center gap-2 px-4 py-3 border shadow-lg transition-all ${
              advisorOpen
                ? "bg-background border-accent text-accent"
                : warnCount > 0
                  ? "bg-amber-950/80 border-amber-600 text-amber-300 animate-pulse"
                  : "bg-background/90 border-primary/40 text-muted-foreground hover:text-accent hover:border-accent"
            }`}>
            <span className="text-base">🤖</span>
            <span className="text-[10px] uppercase tracking-wide font-medium">Советник</span>
            {!advisorOpen && (warnCount > 0) && (
              <span className="w-4 h-4 rounded-full bg-amber-500 text-black text-[9px] flex items-center justify-center font-bold">
                {warnCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Sticky bottom total bar ── */}
      {grandTotal > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-primary/30 bg-background/95 backdrop-blur-sm">
          <div className="container mx-auto px-4 md:px-10 max-w-7xl py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-5">
              <div>
                <p className="text-[9px] text-primary/50 uppercase tracking-wide">К оплате</p>
                <p className="font-serif text-xl text-accent">{fmt(finalTotal)} ₽</p>
              </div>
              {(discountAmount > 0 || serviceChargeAmount > 0) && (
                <div className="hidden sm:flex flex-col gap-0.5">
                  {discountAmount > 0 && (
                    <p className="text-[10px] text-emerald-400">−{fmt(discountAmount)} ₽ скидка 🎂</p>
                  )}
                  {serviceChargeAmount > 0 && (
                    <p className="text-[10px] text-amber-400/80">+{fmt(serviceChargeAmount)} ₽ сбор {serviceChargePct * 100}%</p>
                  )}
                </div>
              )}
              {guests > 0 && !discountAmount && !serviceChargeAmount && (
                <div className="hidden sm:block">
                  <p className="text-[9px] text-primary/50 uppercase tracking-wide">На человека</p>
                  <p className="text-sm text-muted-foreground">~{fmt(finalPerPerson)} ₽</p>
                </div>
              )}
            </div>
            <button onClick={scrollToSummary}
              className="bg-primary text-primary-foreground px-6 py-3 text-[10px] tracking-[0.2em] uppercase font-medium hover:bg-accent transition-colors">
              Оформить →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
